import { Protocol as ControlProtocol } from '@studio/models/control';
import { Protocol as VideoProtocol } from '@studio/models/video';
import type { Discovery } from './types.js';

/**
 * The network host that identifies the physical device behind a Discovery
 * entry, regardless of which discovery mechanism reported it. NDI discoveries
 * carry a host on `.video`; ONVIF ("web") discoveries carry one on
 * `.control`. Every other protocol (Local/RTSP/IRIS) has no host comparable
 * across mechanisms and is excluded from cross-protocol reconciliation.
 */
export const discoveryHostKey = (discovery: Discovery): string | undefined => {
  if ('video' in discovery && discovery.video?.protocol === VideoProtocol.NDI) {
    const host = (discovery.video as { host?: string }).host;
    return host && host.trim() !== '' ? host : undefined;
  }

  if ('control' in discovery && discovery.control?.protocol === ControlProtocol.ONVIF) {
    const host = (discovery.control as { host?: string }).host;
    return host && host.trim() !== '' ? host : undefined;
  }

  return undefined;
};

const discoveryProtocolLabel = (discovery: Discovery): string | undefined => {
  if ('video' in discovery) {
    return discovery.video?.protocol;
  }

  if ('control' in discovery) {
    return discovery.control?.protocol;
  }

  return undefined;
};

/** A detected discovery mismatch: two entries reconciled to one physical host. */
export interface DiscoveryMismatch {
  readonly hostKey: string;
  readonly primaryProtocol: string;
  readonly secondaryProtocol: string;
  readonly primaryName: string;
  readonly secondaryName: string;
}

/**
 * Detect "Web/NDI mislabel" collisions (ENG-4322): two Discovery entries that
 * report the SAME physical host over DIFFERENT protocols with DIFFERENT
 * names. Nothing reconciles this today — queries.ts keys each Discovery by
 * `protocol + host`, so an NDI-reported camera and a web(ONVIF)-reported
 * camera behind the same host never collide into one entry; they simply
 * appear twice with no signal they might be the same device (gap-silent).
 * Same-protocol duplicates (the existing multi-proxy embedded-camera case) and
 * identically-labeled cross-protocol pairs are intentionally NOT mismatches.
 */
export const detectDiscoveryMismatches = (
  discoveries: readonly Discovery[]
): DiscoveryMismatch[] => {
  const byHost = new Map<string, Discovery[]>();

  for (const discovery of discoveries) {
    const hostKey = discoveryHostKey(discovery);
    if (hostKey === undefined) {
      continue;
    }

    const bucket = byHost.get(hostKey);
    if (bucket) {
      bucket.push(discovery);
    } else {
      byHost.set(hostKey, [discovery]);
    }
  }

  const mismatches: DiscoveryMismatch[] = [];

  for (const [hostKey, bucket] of byHost) {
    for (let i = 0; i < bucket.length; i++) {
      const primary = bucket[i];
      if (!primary) {
        continue;
      }

      for (let j = i + 1; j < bucket.length; j++) {
        const secondary = bucket[j];
        if (!secondary) {
          continue;
        }

        const primaryProtocol = discoveryProtocolLabel(primary);
        const secondaryProtocol = discoveryProtocolLabel(secondary);

        if (primaryProtocol === secondaryProtocol) {
          continue; // same-protocol duplicate — handled by the existing multi-proxy merge.
        }

        if (primary.name === secondary.name) {
          continue; // identical label — nothing to reconcile.
        }

        mismatches.push({
          hostKey,
          primaryProtocol: primaryProtocol ?? 'unknown',
          secondaryProtocol: secondaryProtocol ?? 'unknown',
          primaryName: primary.name,
          secondaryName: secondary.name
        });
      }
    }
  }

  return mismatches;
};

/** Per-session dedupe state: one flag per reconciled host key. */
export const createDiscoveryMismatchDedupeState = (): Set<string> => new Set();

/**
 * sessionStorage key holding the reconciled hosts already flagged this session.
 *
 * ENG-4322: a mismatch is keyed by the RECONCILED host (device identity), not
 * by surface — the same physical device mislabeled across NDI/web discovery is
 * one signal regardless of which surface's pass first noticed it.
 */
export const DISCOVERY_MISMATCH_HOSTS_SESSION_KEY = 'analytics.discovery.mismatch_hosts';

/** The read half of Web Storage this module needs — narrowed so tests can pass a fake. */
type MismatchDedupeReader = Pick<Storage, 'getItem'>;
/** The write half of Web Storage this module needs. */
type MismatchDedupeWriter = Pick<Storage, 'setItem'>;

const defaultStorage = (): Storage | undefined =>
  typeof window === 'undefined' ? undefined : window.sessionStorage;

/**
 * Rehydrate the dedupe set from sessionStorage.
 *
 * WHY this is a module function and not an inline `useRef` initializer: the
 * single-fire contract has to survive a REMOUNT of the selector, and the only
 * thing that carries it across one is this stored host list. Inline in the
 * component the rehydrate/persist pair was reachable only by mounting the whole
 * Selector, so no lane could red when it broke — a regression that dropped the
 * restore would re-fire discovery_mismatch on every remount and silently
 * multiply the event, which reads on a dashboard as a spike in mislabeled
 * cameras rather than as a telemetry defect (rule: ai-code-requires-tests,
 * ai.knowledge/global).
 *
 * Fails OPEN to an empty set on absent/corrupt/non-array payloads: losing the
 * dedupe costs a duplicate event, whereas throwing here would break the whole
 * discovery pass that owns the emit.
 */
export const restoreDiscoveryMismatchDedupeState = (
  storage: MismatchDedupeReader | undefined = defaultStorage()
): Set<string> => {
  if (!storage) {
    return createDiscoveryMismatchDedupeState();
  }

  try {
    const raw = storage.getItem(DISCOVERY_MISMATCH_HOSTS_SESSION_KEY);
    if (raw === null || raw === '') {
      return createDiscoveryMismatchDedupeState();
    }

    const hosts: unknown = JSON.parse(raw);
    if (!Array.isArray(hosts)) {
      return createDiscoveryMismatchDedupeState();
    }

    return new Set(hosts.filter((host): host is string => typeof host === 'string'));
  } catch {
    return createDiscoveryMismatchDedupeState();
  }
};

/**
 * Persist the flagged hosts so a remount rehydrates the same dedupe set.
 * Silently no-ops without storage (SSR / a quota-full or private-mode write) —
 * a failed persist degrades to a duplicate event, never to a broken pass.
 */
export const persistDiscoveryMismatchDedupeState = (
  seen: ReadonlySet<string>,
  storage: MismatchDedupeWriter | undefined = defaultStorage()
): void => {
  if (!storage) {
    return;
  }

  try {
    storage.setItem(DISCOVERY_MISMATCH_HOSTS_SESSION_KEY, JSON.stringify(Array.from(seen)));
  } catch {
    // Ignore: see the doc comment — a lost persist costs one duplicate event.
  }
};

/**
 * Detect mismatches in the current discovery pass and invoke `onMismatch` for
 * every NEW one (a host key already in `seen` is not re-flagged). Returns the
 * count emitted, matching the settle/emit shape of discovery-pass.ts so the
 * caller (the Selector effect, or an e2e test probe) can share one code path.
 */
export const emitDiscoveryMismatches = (
  discoveries: readonly Discovery[],
  seen: Set<string>,
  onMismatch: (mismatch: DiscoveryMismatch) => void
): number => {
  let emitted = 0;

  for (const mismatch of detectDiscoveryMismatches(discoveries)) {
    if (seen.has(mismatch.hostKey)) {
      continue;
    }

    seen.add(mismatch.hostKey);
    onMismatch(mismatch);
    emitted++;
  }

  return emitted;
};
